import {
  Appointment,
  AppointmentStatus,
  BillStatus,
  BillType,
  UserRole,
} from '@/types/types';

export type OpdPayoutExpenseSummary = {
  id: string;
  expenseNumber: string | null;
  amount: number;
  workflowStatus: string;
};

export function sumOpdConsultNet(appointment: Appointment): number {
  let net = 0;
  for (const bill of appointment.bills ?? []) {
    if (bill.status === BillStatus.CANCELLED) continue;
    const opdItems = (bill.billItems ?? []).filter(
      (item) => item.itemType === BillType.OPD_CONSULTATION
    );
    if (opdItems.length > 0) {
      for (const item of opdItems) {
        net += Math.max(0, Number(item.totalPrice || 0));
      }
    } else if (Number(bill.totalAmount || 0) > 0) {
      // Fallback when billItems are missing from list payload (legacy rows).
      net += Number(bill.totalAmount);
    }
  }
  return net;
}

export function getOpdPayoutExpense(
  appointment: Appointment
): OpdPayoutExpenseSummary | null {
  const rows = (appointment as Appointment & { opdPayoutExpenses?: OpdPayoutExpenseSummary[] })
    .opdPayoutExpenses;
  return rows?.[0] ?? null;
}

export function canRecordOpdPayoutRole(role: string | undefined): boolean {
  return (
    role === UserRole.RECEPTIONIST ||
    role === UserRole.HOSPITAL_ADMIN ||
    role === UserRole.OPERATIONS_MANAGER ||
    role === UserRole.BUSINESS_ANALYST
  );
}

function isOpdPayoutVisitStatus(status: AppointmentStatus): boolean {
  // Bill is often created at queue check-in (CONFIRMED) before status becomes DIAGNOSED.
  return (
    status === AppointmentStatus.CONFIRMED ||
    status === AppointmentStatus.DIAGNOSED
  );
}

export function isOpdPayoutEligible(appointment: Appointment): boolean {
  if (getOpdPayoutExpense(appointment)) return false;
  if (!isOpdPayoutVisitStatus(appointment.status)) return false;
  if (sumOpdConsultNet(appointment) <= 0) return false;
  const doc = appointment.doctor;
  if (!doc) return false;
  if (doc.role !== UserRole.DOCTOR) return false;
  if ((doc as { isSalaried?: boolean }).isSalaried) return false;
  return true;
}

export function defaultOpdPayoutServiceMonth(appointment: Appointment): string {
  const bill = appointment.bills?.[0];
  if (!bill?.billDate) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  const d = new Date(bill.billDate);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
